import { useContext, useState, useRef, useEffect } from 'react';
import { CatalogDispatchContext, CatalogStateContext } from '../../store/CatalogContext';
import useFuzzySearch from '../../hooks/useFuzzySearch';
import SearchIcon from '../../assets/search-icon.svg?react';
import useSearchHandler from '../../hooks/useSearchHandler';
import type { Product } from '../../types/product';

interface SearchSuggestionProps {
  group: 'purchased' | 'related' | 'fullCatalog';
  prefix: string;
  filteredProducts: Product[];
}

export default function SearchAndSuggestion({ group, prefix, filteredProducts }: SearchSuggestionProps) {
  const catalogDispatch = useContext(CatalogDispatchContext);
  const catalogState = useContext(CatalogStateContext); // full state if needed by searchHandler

  const [searchState, setSearchState] = useState({
    query: '',
    appliedSuggestions: false,
    showSuggestions: false,
  });

  const dropdownEl = useRef<HTMLDivElement>(null);

  // search results, this needs to be the top level set of products
  const { results: searchedProducts, isDebouncing } = useFuzzySearch(filteredProducts, searchState.query);

  useEffect(() => {
    if (!isDebouncing && searchState.query.length > 1 && !searchState.appliedSuggestions) {
      setSearchState((prev) => ({
        ...prev,
        showSuggestions: true,
      }));
    }
  }, [isDebouncing, searchState.query, searchState.appliedSuggestions]);

  // Outside click handler using ref containment & pointer events
  useEffect(() => {
    if (!searchState.showSuggestions) return;

    const handlePointerDown = (event: MouseEvent | PointerEvent) => {
      const target = event.target as Node;

      if (dropdownEl.current?.contains(target)) return;

      setSearchState((prev) => ({
        ...prev,
        showSuggestions: false,
      }));
    };

    // using pointerdown because it immediately fires, and we want the widget hidden immediately
    document.addEventListener('pointerdown', handlePointerDown);

    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [searchState.showSuggestions]);

  const searchHandler = useSearchHandler(catalogState, catalogDispatch, group);

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchState((prev) => ({
      ...prev,
      query: e.target.value,
      appliedSuggestions: false,
    }));
  };

  const handleSearch = () => {
    if (searchState.query.length < 2) return; // min 2 chars

    searchHandler(searchState.query);

    setSearchState((prev) => ({
      ...prev,
      // query: '',
      showSuggestions: false,
    }));
  };

  const handleApplySuggestion = (suggestion: string) => {
    searchHandler(suggestion);

    setSearchState((prev) => ({
      ...prev,
      query: suggestion,
      showSuggestions: false,
      appliedSuggestions: true,
    }));
  };

  const suggestionOptions = searchedProducts
    .map((product, index) => {
      if (index >= 10) return null; // limit to 10 suggestions
      return (
        <button
          className="dropdown-item text-wrap"
          type="button"
          key={product.id}
          onClick={() => handleApplySuggestion(product.name)}
        >
          {product.name}
        </button>
      );
    })
    .filter(Boolean);

  return (
    <div className="search-wrapper position-relative" ref={dropdownEl}>
      <div className="input-group search-input-group">
        <input
          id={`${prefix}-search-input`}
          className="form-control input-search"
          name="search"
          type="search"
          aria-label="Search"
          placeholder="Search..."
          value={searchState.query}
          onChange={handleSearchTermChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }}
        />
        <button className="btn btn-search" type="button" onClick={handleSearch}>
          <SearchIcon />
        </button>
      </div>

      {searchState.showSuggestions && (
        <div className="dropdown-menu search-suggestions show w-100">
          {searchedProducts.length > 0 ? (
            suggestionOptions
          ) : (
            <option className="dropdown-item-text text-muted" disabled>
              No matches found
            </option>
          )}
        </div>
      )}
    </div>
  );
}
